import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from "@/hooks/use-toast";
import { usersService } from "@/services/users.service";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Settings,
  User,
  Bell,
  Shield,
  Database,
  Save,
  Key,

} from "lucide-react";


const Parametres = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    nom: '',
    email: '',
    organisation: '',
    description: ''
  });

  const [preferences, setPreferences] = useState({
    notifications_email: true,
    notifications_push: false,
    auto_analyze: true,
    save_filters: true
  });

  const [scoringConfig, setScoringConfig] = useState({
    poids_competences: 40,
    poids_experience: 30,
    poids_formation: 20,
    poids_langues: 10,
    seuil_pertinence: 60
  });

  useEffect(() => {
    if (user) {
      setProfile(prev => ({
        ...prev,
        email: user.email || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await usersService.getMyPreferences();
        if (prefs.profile) {
          setProfile(prev => ({
            ...prev,
            nom: prefs.profile?.nom || '',
            organisation: prefs.profile?.organisation || '',
            description: prefs.profile?.description || '',
          }));
        }
        if (prefs.preferences) {
          setPreferences({
            notifications_email: prefs.preferences.notificationsEmail ?? true,
            notifications_push: prefs.preferences.notificationsPush ?? false,
            auto_analyze: prefs.preferences.autoAnalyze ?? true,
            save_filters: prefs.preferences.saveFilters ?? true,
          });
        }
        if (prefs.scoringConfig) {
          setScoringConfig({
            poids_competences: prefs.scoringConfig.poidsCompetences ?? 40,
            poids_experience: prefs.scoringConfig.poidsExperience ?? 30,
            poids_formation: prefs.scoringConfig.poidsFormation ?? 20,
            poids_langues: prefs.scoringConfig.poidsLangues ?? 10,
            seuil_pertinence: prefs.scoringConfig.seuilPertinence ?? 60,
          });
        }
      } catch {
        // First load or no preferences yet — use defaults
      }
    };
    loadPreferences();
  }, []);

  const saveProfile = async () => {
    setLoading(true);
    try {
      await usersService.updateMyPreferences({
        profile: {
          nom: profile.nom,
          organisation: profile.organisation,
          description: profile.description,
        },
      });
      toast({
        title: "Profil sauvegardé",
        description: "Vos informations ont été mises à jour avec succès.",
      });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le profil.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setLoading(true);
    try {
      await usersService.updateMyPreferences({
        preferences: {
          notificationsEmail: preferences.notifications_email,
          notificationsPush: preferences.notifications_push,
          autoAnalyze: preferences.auto_analyze,
          saveFilters: preferences.save_filters,
        },
      });
      toast({
        title: "Préférences sauvegardées",
        description: "Vos préférences ont été mises à jour.",
      });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les préférences.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const saveScoringConfig = async () => {
    setLoading(true);
    try {
      await usersService.updateMyPreferences({
        scoringConfig: {
          poidsCompetences: scoringConfig.poids_competences,
          poidsExperience: scoringConfig.poids_experience,
          poidsFormation: scoringConfig.poids_formation,
          poidsLangues: scoringConfig.poids_langues,
          seuilPertinence: scoringConfig.seuil_pertinence,
        },
      });
      toast({
        title: "Configuration sauvegardée",
        description: "La configuration de scoring a été mise à jour.",
      });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder la configuration.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center">
          <Settings className="h-8 w-8 mr-3 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Paramètres</h1>
            <p className="text-muted-foreground">
              Gérez vos préférences et configurations
            </p>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Profil utilisateur */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <User className="h-5 w-5 mr-2" />
                Profil utilisateur
              </CardTitle>
              <CardDescription>
                Gérez vos informations personnelles
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom complet</Label>
                  <Input
                    id="nom"
                    value={profile.nom}
                    onChange={(e) => setProfile(prev => ({ ...prev, nom: e.target.value }))}
                    placeholder="Votre nom complet"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="votre@email.com"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="organisation">Organisation</Label>
                <Input
                  id="organisation"
                  value={profile.organisation}
                  onChange={(e) => setProfile(prev => ({ ...prev, organisation: e.target.value }))}
                  placeholder="Nom de votre organisation"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={profile.description}
                  onChange={(e) => setProfile(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Décrivez votre rôle ou activité..."
                  rows={3}
                />
              </div>
              <Button onClick={saveProfile} disabled={loading} className="w-full md:w-auto">
                <Save className="h-4 w-4 mr-2" />
                Sauvegarder le profil
              </Button>
            </CardContent>
          </Card>

          {/* Préférences */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bell className="h-5 w-5 mr-2" />
                Préférences
              </CardTitle>
              <CardDescription>
                Configurez vos notifications et comportements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Notifications par email</Label>
                  <div className="text-sm text-muted-foreground">
                    Recevoir des notifications par email
                  </div>
                </div>
                <Switch
                  checked={preferences.notifications_email}
                  onCheckedChange={(checked) => setPreferences(prev => ({ 
                    ...prev, 
                    notifications_email: checked 
                  }))}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Notifications push</Label>
                  <div className="text-sm text-muted-foreground">
                    Recevoir des notifications push dans le navigateur
                  </div>
                </div>
                <Switch
                  checked={preferences.notifications_push}
                  onCheckedChange={(checked) => setPreferences(prev => ({ 
                    ...prev, 
                    notifications_push: checked 
                  }))}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Analyse automatique</Label>
                  <div className="text-sm text-muted-foreground">
                    Analyser automatiquement les nouveaux documents importés
                  </div>
                </div>
                <Switch
                  checked={preferences.auto_analyze}
                  onCheckedChange={(checked) => setPreferences(prev => ({ 
                    ...prev, 
                    auto_analyze: checked 
                  }))}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Sauvegarder les filtres</Label>
                  <div className="text-sm text-muted-foreground">
                    Mémoriser vos filtres de recherche
                  </div>
                </div>
                <Switch
                  checked={preferences.save_filters}
                  onCheckedChange={(checked) => setPreferences(prev => ({ 
                    ...prev, 
                    save_filters: checked 
                  }))}
                />
              </div>
              <Button onClick={savePreferences} disabled={loading} className="w-full md:w-auto">
                <Save className="h-4 w-4 mr-2" />
                Sauvegarder les préférences
              </Button>
            </CardContent>
          </Card>

          {/* Configuration de scoring */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Database className="h-5 w-5 mr-2" />
                Configuration du scoring
              </CardTitle>
              <CardDescription>
                Définissez les pondérations par défaut pour l'analyse des CV
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="competences">Poids Compétences (%)</Label>
                  <Input
                    id="competences"
                    type="number"
                    min="0"
                    max="100"
                    value={scoringConfig.poids_competences}
                    onChange={(e) => setScoringConfig(prev => ({ 
                      ...prev, 
                      poids_competences: parseInt(e.target.value) || 0 
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="experience">Poids Expérience (%)</Label>
                  <Input
                    id="experience"
                    type="number"
                    min="0"
                    max="100"
                    value={scoringConfig.poids_experience}
                    onChange={(e) => setScoringConfig(prev => ({ 
                      ...prev, 
                      poids_experience: parseInt(e.target.value) || 0 
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="formation">Poids Formation (%)</Label>
                  <Input
                    id="formation"
                    type="number"
                    min="0"
                    max="100"
                    value={scoringConfig.poids_formation}
                    onChange={(e) => setScoringConfig(prev => ({ 
                      ...prev, 
                      poids_formation: parseInt(e.target.value) || 0 
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="langues">Poids Langues (%)</Label>
                  <Input
                    id="langues"
                    type="number"
                    min="0"
                    max="100"
                    value={scoringConfig.poids_langues}
                    onChange={(e) => setScoringConfig(prev => ({ 
                      ...prev, 
                      poids_langues: parseInt(e.target.value) || 0 
                    }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="seuil">Seuil de pertinence (%)</Label>
                <Input
                  id="seuil"
                  type="number"
                  min="0"
                  max="100"
                  value={scoringConfig.seuil_pertinence}
                  onChange={(e) => setScoringConfig(prev => ({ 
                    ...prev, 
                    seuil_pertinence: parseInt(e.target.value) || 0 
                  }))}
                />
                <div className="text-sm text-muted-foreground">
                  Score minimum pour qu'un CV soit considéré comme pertinent
                </div>
              </div>
              <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
                Total actuel: {scoringConfig.poids_competences + scoringConfig.poids_experience + scoringConfig.poids_formation + scoringConfig.poids_langues}% 
                {(scoringConfig.poids_competences + scoringConfig.poids_experience + scoringConfig.poids_formation + scoringConfig.poids_langues) !== 100 && " (doit être égal à 100%)"}
              </div>
              <Button onClick={saveScoringConfig} disabled={loading} className="w-full md:w-auto">
                <Save className="h-4 w-4 mr-2" />
                Sauvegarder la configuration
              </Button>
            </CardContent>
          </Card>


          {/* Sécurité */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Sécurité
              </CardTitle>
              <CardDescription>
                Gérez vos paramètres de sécurité et d'authentification
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <Key className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="font-medium">Mot de passe</div>
                    <div className="text-sm text-muted-foreground">
                      Dernière modification il y a 30 jours
                    </div>
                  </div>
                </div>
                <Button variant="outline">
                  Modifier
                </Button>
              </div>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="font-medium">Authentification à deux facteurs</div>
                    <div className="text-sm text-muted-foreground">
                      Non configurée
                    </div>
                  </div>
                </div>
                <Button variant="outline">
                  Configurer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Parametres;